import React from "react";
import { Ruler } from "lucide-react";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";

// RU ring size = inner diameter (mm), 15–22.5 in 0.5 steps. EU = circumference (mm); US & UK approximate.
const RING = [
  { ru: "15", eu: "47", uk: "H½", us: "4" },
  { ru: "15.5", eu: "49", uk: "I½", us: "4½" },
  { ru: "16", eu: "50", uk: "K", us: "5¼" },
  { ru: "16.5", eu: "52", uk: "L½", us: "6" },
  { ru: "17", eu: "53", uk: "M½", us: "6½" },
  { ru: "17.5", eu: "55", uk: "O", us: "7" },
  { ru: "18", eu: "57", uk: "P", us: "7¾" },
  { ru: "18.5", eu: "58", uk: "Q½", us: "8½" },
  { ru: "19", eu: "60", uk: "S", us: "9" },
  { ru: "19.5", eu: "61", uk: "T", us: "9½" },
  { ru: "20", eu: "63", uk: "U", us: "10" },
  { ru: "20.5", eu: "64", uk: "V", us: "10½" },
  { ru: "21", eu: "66", uk: "X", us: "11½" },
  { ru: "21.5", eu: "68", uk: "Y", us: "12" },
  { ru: "22", eu: "69", uk: "Z", us: "12½" },
  { ru: "22.5", eu: "71", uk: "Z½", us: "13" },
];

// Women's footwear. EU is primary; UK & US approximate.
const SHOE = [
  { eu: "35", uk: "2½", us: "4½" },
  { eu: "35.5", uk: "3", us: "5" },
  { eu: "36", uk: "3½", us: "5½" },
  { eu: "37", uk: "4", us: "6½" },
  { eu: "37.5", uk: "4½", us: "7" },
  { eu: "38", uk: "5", us: "7½" },
  { eu: "38.5", uk: "5½", us: "8" },
  { eu: "39", uk: "6", us: "8½" },
  { eu: "40", uk: "6½", us: "9" },
  { eu: "40.5", uk: "7", us: "9½" },
  { eu: "41", uk: "7½", us: "10" },
  { eu: "42", uk: "8", us: "11" },
];

const RING_COLS = [["RU", "ru"], ["EU", "eu"], ["UK", "uk"], ["US", "us"]];
const SHOE_COLS = [["EU", "eu"], ["UK", "uk"], ["US", "us"]];

// Women's ready-to-wear. International (XS–XL) with EU / UK / US equivalents.
const CLOTHING = [
  { intl: "XS", eu: "34", uk: "6", us: "2" },
  { intl: "S", eu: "36", uk: "8", us: "4" },
  { intl: "S", eu: "38", uk: "10", us: "6" },
  { intl: "M", eu: "40", uk: "12", us: "8" },
  { intl: "M", eu: "42", uk: "14", us: "10" },
  { intl: "L", eu: "44", uk: "16", us: "12" },
  { intl: "XL", eu: "46", uk: "18", us: "14" },
  { intl: "XL", eu: "48", uk: "20", us: "16" },
];

const CLOTHING_COLS = [["Size", "intl"], ["EU", "eu"], ["UK", "uk"], ["US", "us"]];

const TABLES = {
  ring: { rows: RING, cols: RING_COLS },
  shoe: { rows: SHOE, cols: SHOE_COLS },
  clothing: { rows: CLOTHING, cols: CLOTHING_COLS },
};

export const SizeGuide = ({ type = "ring" }) => {
  const { t } = useApp();
  const { rows, cols } = TABLES[type] || TABLES.ring;
  const titleKey = { ring: "sizeGuideTitle", shoe: "shoeSizeGuideTitle", clothing: "clothingSizeGuideTitle" }[type];
  const noteKey = { ring: "sizeGuideNote", shoe: "shoeSizeGuideNote", clothing: "clothingSizeGuideNote" }[type];
  const title = t.shop[titleKey];
  const note = t.shop[noteKey];

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid={`${type}-size-guide-trigger`}
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-muted hover:text-ink transition-colors"
        >
          <Ruler className="h-3.5 w-3.5" strokeWidth={1.5} />
          {t.shop.sizeGuide}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-paper border-line max-w-md" data-testid={`${type}-size-guide-content`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light">{title}</DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-muted leading-relaxed -mt-1">{note}</p>
        <div className="max-h-[60vh] overflow-y-auto -mr-2 pr-2 mt-2">
          <table className="w-full text-left tabular-nums">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.14em] text-muted border-b border-line">
                {cols.map(([label]) => (
                  <th key={label} className="py-2 font-medium">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="font-sans text-sm text-ink/80">
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-line/50">
                  {cols.map(([label, key]) => (
                    <td key={label} className="py-2">{r[key]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SizeGuide;
